import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Dialog, DialogFooter, PrimaryButton, TextField, DefaultButton, Stack, RatingSize, Rating, Checkbox, Link, Text } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import { getReviewModalDialogProps } from '@shared/utils/reviewsUtils';
import greyExclamationMark from '@shared/images/greyExclamationMark.svg';
export const renderOfferInfoSection = (appIcon, appTitle, appPublisher)=>{
    return /*#__PURE__*/ _jsxs("div", {
        className: "ReviewActionModalAppDetails",
        children: [
            /*#__PURE__*/ _jsx("img", {
                className: "ReviewActionModalAppIcon",
                src: appIcon,
                alt: `${appTitle} icon`
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                className: "ReviewActionModalAppNameAndPublisher",
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        className: "ReviewActionModalAppName",
                        children: appTitle
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        className: "ReviewActionModalAppPublisher",
                        children: appPublisher
                    })
                ]
            })
        ]
    });
};
export const renderRatingSection = (context, IsvReplyExists, curRating, handleRatingChange)=>{
    return /*#__PURE__*/ _jsxs(Stack, {
        className: "ReviewActionModalRating",
        children: [
            /*#__PURE__*/ _jsxs(Text, {
                className: "ReviewActionModalRatingTitle",
                children: [
                    context.loc('ReviewActionModal_RatingSectionTitle', 'Rating'),
                    ' ',
                    /*#__PURE__*/ _jsx(Text, {
                        className: "ReviewActionModalMandatoryFieldSign",
                        children: "*"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Rating, {
                allowZeroStars: true,
                disabled: IsvReplyExists,
                max: 5,
                size: RatingSize.Large,
                rating: curRating,
                ariaLabel: context.loc('ReviewActionModal_RatingAriaLabel', 'Rating'),
                ariaLabelFormat: context.loc('ReviewActionModal_RatingAriaLabelFormat', `{0} of {1} stars`),
                getAriaLabel: (rating, max)=>context.loc('ReviewActionModal_RatingAriaLabelReadOnly', `Rating value is ${rating} of ${max}`),
                onChange: (event, rating)=>handleRatingChange(rating)
            })
        ]
    });
};
export const renderLinksSection = (context)=>{
    return /*#__PURE__*/ _jsxs("div", {
        className: "ReviewActionModalLinks",
        children: [
            /*#__PURE__*/ _jsx(Link, {
                target: "_blank",
                href: Constants.MicrosoftTermsURL,
                children: context.loc('ReviewActionModal_Terms', 'Terms')
            }),
            /*#__PURE__*/ _jsx(Text, {
                className: "LinksSeperator",
                children: " | "
            }),
            /*#__PURE__*/ _jsx(Link, {
                target: "_blank",
                href: Constants.MicrosoftPrivacyStatementURL,
                children: context.loc('ReviewActionModal_PrivacyStatement', 'Privacy statement')
            })
        ]
    });
};
export const renderWriteDialogButtonsSection = (context, curRating, handleSubmitReview, closeModal)=>{
    return /*#__PURE__*/ _jsxs(DialogFooter, {
        children: [
            /*#__PURE__*/ _jsx(PrimaryButton, {
                onClick: ()=>handleSubmitReview(),
                disabled: !(!!curRating || curRating === 0),
                text: context.loc('ReviewActionModal_Submit', 'Submit')
            }),
            /*#__PURE__*/ _jsx(DefaultButton, {
                onClick: ()=>closeModal(),
                text: context.loc('Cancel', 'Cancel')
            })
        ]
    });
};
export const renderEditDialogButtonsSection = (context, IsvReplyExists, handleDeleteReview, handleSubmitReview, closeModal)=>{
    return /*#__PURE__*/ _jsxs(DialogFooter, {
        children: [
            /*#__PURE__*/ _jsx(PrimaryButton, {
                onClick: ()=>handleDeleteReview(),
                text: context.loc('Rating_DeleteButton', 'Delete')
            }),
            /*#__PURE__*/ _jsx(PrimaryButton, {
                onClick: ()=>handleSubmitReview(),
                disabled: IsvReplyExists,
                text: context.loc('Rating_UpdateButton', 'Update')
            }),
            /*#__PURE__*/ _jsx(DefaultButton, {
                onClick: ()=>closeModal(),
                text: context.loc('Cancel', 'Cancel')
            })
        ]
    });
};
export const ReviewActionModal = ({ context, title, appTitle, appPublisher, userEmail, userDisplayName, appIcon, curRating, curReviewTitle, curReviewContent, isAnonymous, userReview, closeModal, handleRatingChange, handleReviewTitleChange, handleReviewContentChange, handleSendEmailCheckbox, handleDisplayUserNameCheckbox, handleSubmitReview, handleDeleteReview })=>{
    const dialogProps = getReviewModalDialogProps(title);
    const isEditMode = !!userReview;
    const IsvReplyExists = isEditMode && !!userReview.isv_reply;
    return /*#__PURE__*/ _jsx(Dialog, {
        hidden: false,
        onDismiss: ()=>closeModal(),
        dialogContentProps: dialogProps.dialogContentProps,
        maxWidth: dialogProps.maxWidth,
        modalProps: dialogProps.modalProps,
        children: /*#__PURE__*/ _jsxs(Stack, {
            tokens: {
                childrenGap: 12
            },
            styles: {
                root: {
                    width: 500
                }
            },
            children: [
                renderOfferInfoSection(appIcon, appTitle, appPublisher),
                IsvReplyExists && /*#__PURE__*/ _jsxs("div", {
                    className: "ReviewActionModalEditComment",
                    children: [
                        /*#__PURE__*/ _jsx("img", {
                            className: "ReviewActionModalEditCommentImg",
                            src: greyExclamationMark
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            className: "ReviewActionModalEditCommentText",
                            children: context.loc('ReviewActionModal_EditingNotAllowedIsvReply', 'Editing this comment is disabled because it has already\nreceived a response. You can still delete it.')
                        })
                    ]
                }),
                renderRatingSection(context, IsvReplyExists, curRating, handleRatingChange),
                /*#__PURE__*/ _jsx(TextField, {
                    label: context.loc('ReviewActionModal_ReviewTitle', 'Review title'),
                    placeholder: context.loc('ReviewActionModal_ReviewTitle', 'Review title'),
                    value: curReviewTitle,
                    disabled: IsvReplyExists,
                    inputClassName: "reviewActionModalTitleTextField",
                    onChange: (event, newValue)=>handleReviewTitleChange(newValue)
                }),
                /*#__PURE__*/ _jsx(TextField, {
                    label: context.loc('ReviewActionModal_ReviewDescription', 'Review description'),
                    multiline: true,
                    rows: 3,
                    placeholder: context.loc('ReviewActionModal_ReviewDescriptionPlaceholder', 'Write your review'),
                    value: curReviewContent,
                    disabled: IsvReplyExists,
                    onChange: (event, newValue)=>handleReviewContentChange(newValue)
                }),
                /*#__PURE__*/ _jsx(Checkbox, {
                    label: context.locParams('ReviewActionModal_EmailCheckboxText', [
                        appPublisher,
                        userEmail
                    ], `I agree to have ${appPublisher} email me at ${userEmail} about my feedback.`),
                    disabled: IsvReplyExists,
                    onChange: ()=>handleSendEmailCheckbox()
                }),
                /*#__PURE__*/ _jsx(Checkbox, {
                    label: context.locParams('ReviewActionModal_DisplayNameCheckboxText', [
                        userDisplayName
                    ], `Post my review publicly as ${userDisplayName} (if this is unchecked, the review will be posted as 'Anonymous')`),
                    checked: !isAnonymous,
                    defaultChecked: true,
                    disabled: IsvReplyExists,
                    onChange: ()=>handleDisplayUserNameCheckbox()
                }),
                renderLinksSection(context),
                isEditMode ? renderEditDialogButtonsSection(context, IsvReplyExists, handleDeleteReview, handleSubmitReview, closeModal) : renderWriteDialogButtonsSection(context, curRating, handleSubmitReview, closeModal)
            ]
        })
    });
};
